import React from 'react';
import { Link } from 'react-router-dom';
import '../legal/legal.css';
import {
    HelpCenterHeroArt,
    FeesIllustration,
    PrivacyShieldIllustration,
} from '../legal/illustrations';
import { openCookieSettings } from '../legal/cookieConsent';
import { useT } from '../i18n/I18nProvider';

// ─────────────────────────────────────────────────────────────────────────────
// Help Center — full-bleed hero (huge illustration with the heading overlaid on
// top), followed by help topics, an FAQ and a prominent support contact band.
// Content and tone are derived from the Impressum / Datenschutz / Cookie pages
// (Seuytel · MainEvents, Walkürenring 4, 38106 Braunschweig; payments via
// Stripe/PayPal; transparent fees; DSGVO rights via the support address).
// ─────────────────────────────────────────────────────────────────────────────

const SUPPORT_EMAIL = 'support@mainevents.seuytel.com';

// ── Brand-gradient topic icons (each gradient id is unique) ──────────────────
const grad = (id: string) => (
    <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#5B8FE8" />
        <stop offset="100%" stopColor="#6C3DD3" />
    </linearGradient>
);

const AccountIcon = () => (
    <svg viewBox="0 0 64 64"><defs>{grad('hc-acc')}</defs>
        <circle cx="32" cy="23" r="12" fill="url(#hc-acc)" />
        <path d="M12 54 a20 20 0 0 1 40 0 Z" fill="url(#hc-acc)" />
    </svg>
);
const TicketIcon = () => (
    <svg viewBox="0 0 64 64"><defs>{grad('hc-tic')}</defs>
        <rect x="8" y="18" width="48" height="28" rx="6" fill="url(#hc-tic)" />
        <circle cx="32" cy="18" r="6" fill="#f6f7fb" />
        <circle cx="32" cy="46" r="6" fill="#f6f7fb" />
        <line x1="32" y1="25" x2="32" y2="39" stroke="#fff" strokeWidth="2" strokeDasharray="2 3" />
    </svg>
);
const ResellIcon = () => (
    <svg viewBox="0 0 64 64"><defs>{grad('hc-res')}</defs>
        <path d="M48 26 A18 18 0 1 0 50 38" fill="none" stroke="url(#hc-res)" strokeWidth="6" strokeLinecap="round" />
        <path d="M49 13 V27 H35" fill="none" stroke="url(#hc-res)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const EventIcon = () => (
    <svg viewBox="0 0 64 64"><defs>{grad('hc-evt')}</defs>
        <rect x="10" y="16" width="44" height="38" rx="8" fill="url(#hc-evt)" />
        <rect x="10" y="16" width="44" height="11" rx="8" fill="#6C3DD3" opacity="0.85" />
        <line x1="22" y1="11" x2="22" y2="21" stroke="#fff" strokeWidth="4" strokeLinecap="round" />
        <line x1="42" y1="11" x2="42" y2="21" stroke="#fff" strokeWidth="4" strokeLinecap="round" />
        {[24, 32, 40].map((x) => [37, 46].map((y) => <circle key={`${x}-${y}`} cx={x} cy={y} r="2.6" fill="#fff" />))}
    </svg>
);
const SeatIcon = () => (
    <svg viewBox="0 0 64 64"><defs>{grad('hc-seat')}</defs>
        <rect x="16" y="14" width="10" height="30" rx="5" fill="url(#hc-seat)" />
        <rect x="16" y="34" width="34" height="10" rx="5" fill="url(#hc-seat)" />
        <rect x="18" y="44" width="6" height="10" rx="3" fill="#6C3DD3" />
        <rect x="42" y="44" width="6" height="10" rx="3" fill="#6C3DD3" />
    </svg>
);
const ChatIcon = () => (
    <svg viewBox="0 0 64 64"><defs>{grad('hc-chat')}</defs>
        <rect x="10" y="16" width="44" height="32" rx="9" fill="url(#hc-chat)" />
        <path d="M20 48 L20 58 L32 48 Z" fill="url(#hc-chat)" />
        {[24, 32, 40].map((x) => <circle key={x} cx={x} cy="32" r="3" fill="#fff" />)}
    </svg>
);

const ReportIcon = () => (
    <svg viewBox="0 0 64 64"><defs>{grad('hc-rep')}</defs>
        <line x1="18" y1="8" x2="18" y2="58" stroke="url(#hc-rep)" strokeWidth="5" strokeLinecap="round" />
        <path d="M20 12 H50 L42 23 L50 34 H20 Z" fill="url(#hc-rep)" />
    </svg>
);
const TrashIcon = () => (
    <svg viewBox="0 0 64 64"><defs>{grad('hc-del')}</defs>
        <rect x="16" y="20" width="32" height="34" rx="6" fill="url(#hc-del)" />
        <rect x="12" y="13" width="40" height="8" rx="4" fill="url(#hc-del)" />
        <rect x="26" y="9" width="12" height="7" rx="3" fill="url(#hc-del)" />
        {[27, 32, 37].map((x) => <line key={x} x1={x} y1="28" x2={x} y2="47" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />)}
    </svg>
);

// White support headset used on the gradient contact band.
const SupportMark = () => (
    <svg viewBox="0 0 64 64" role="img" aria-label="Support">
        <path d="M14 38 a18 18 0 0 1 36 0" fill="none" stroke="#fff" strokeWidth="5" strokeLinecap="round" />
        <rect x="10" y="36" width="9" height="17" rx="4" fill="#fff" />
        <rect x="45" y="36" width="9" height="17" rx="4" fill="#fff" />
        <path d="M49 53 C49 60 43 62 37 62" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" />
        <circle cx="34" cy="62" r="4" fill="#fff" />
    </svg>
);

type Topic = {
    icon: React.ReactNode;
    titleKey: string;
    descKey: string;
    to?: string; // internal route → <Link>
    href?: string; // same-page anchor → <a>
};

const TOPICS: Topic[] = [
    { icon: <AccountIcon />, titleKey: 'hc.topic.account.title', descKey: 'hc.topic.account.desc', href: '#konto' },
    { icon: <TicketIcon />, titleKey: 'hc.topic.buy.title', descKey: 'hc.topic.buy.desc', href: '#faq' },
    { icon: <ResellIcon />, titleKey: 'hc.topic.resell.title', descKey: 'hc.topic.resell.desc', href: '#faq' },
    { icon: <FeesIllustration />, titleKey: 'hc.topic.fees.title', descKey: 'hc.topic.fees.desc', href: '#faq' },
    { icon: <EventIcon />, titleKey: 'hc.topic.host.title', descKey: 'hc.topic.host.desc', href: '#events-erstellen' },
    { icon: <SeatIcon />, titleKey: 'hc.topic.seat.title', descKey: 'hc.topic.seat.desc', href: '#faq' },
    { icon: <ChatIcon />, titleKey: 'hc.topic.chat.title', descKey: 'hc.topic.chat.desc', href: '#faq' },
    { icon: <ReportIcon />, titleKey: 'hc.topic.report.title', descKey: 'hc.topic.report.desc', to: '/profil-melden' },
    { icon: <TrashIcon />, titleKey: 'hc.topic.delete.title', descKey: 'hc.topic.delete.desc', to: '/profil-loeschen' },
    { icon: <PrivacyShieldIllustration />, titleKey: 'hc.topic.privacy.title', descKey: 'hc.topic.privacy.desc', to: '/datenschutz' },
];

type Faq = { q: string; a: React.ReactNode };

// Renders a translated string, turning **…** segments into <strong>.
const rich = (s: string): React.ReactNode =>
    s.split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));

const supportMail = <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>;

const buildFaqs = (t: (key: string) => string): Faq[] => [
    { q: t('hc.faq.q1'), a: rich(t('hc.faq.a1')) },
    { q: t('hc.faq.q2'), a: rich(t('hc.faq.a2')) },
    { q: t('hc.faq.q3'), a: rich(t('hc.faq.a3')) },
    { q: t('hc.faq.q4'), a: rich(t('hc.faq.a4')) },
    {
        q: t('hc.faq.q5'),
        a: (
            <>
                {rich(t('hc.faq.a5'))} {supportMail}.
            </>
        ),
    },
    { q: t('hc.faq.q6'), a: rich(t('hc.faq.a6')) },
    { q: t('hc.faq.q7'), a: rich(t('hc.faq.a7')) },
    {
        q: t('hc.faq.q8'),
        a: (
            <>
                {rich(t('hc.faq.a8.p1'))}{' '}
                <button type="button" className="se-link-btn" onClick={openCookieSettings} style={{ fontWeight: 700 }}>
                    {t('hc.faq.a8.btn')}
                </button>
                . {rich(t('hc.faq.a8.p2'))} <Link to="/cookies">{t('hc.faq.a8.linkCookies')}</Link>{' '}
                {t('hc.faq.a8.p3')} <Link to="/datenschutz">{t('hc.faq.a8.linkPrivacy')}</Link>.{' '}
                {rich(t('hc.faq.a8.p4'))} {supportMail}.
            </>
        ),
    },
    {
        q: t('hc.faq.q9'),
        a: (
            <>
                {rich(t('hc.faq.a9.p1'))} <Link to="/profil-loeschen">{t('hc.faq.a9.link')}</Link>.{' '}
                {rich(t('hc.faq.a9.p2'))} {supportMail}. {rich(t('hc.faq.a9.p3'))}
            </>
        ),
    },
    {
        q: t('hc.faq.q10'),
        a: (
            <>
                {rich(t('hc.faq.a10.p1'))} <Link to="/profil-melden">{t('hc.faq.a10.link')}</Link>.{' '}
                {rich(t('hc.faq.a10.p2'))}
            </>
        ),
    },
];

export default function HelpCenter() {
    const { t } = useT();
    const faqs = buildFaqs(t);
    return (
        <>
            {/* hero — huge illustration with text overlaid */}
            <section className="se-hc-hero">
                <div className="se-hc-hero-art">
                    <HelpCenterHeroArt />
                </div>
                <div className="se-hc-scrim" />
                <div className="se-hc-hero-inner">
                    <div className="se-hc-hero-text">
                        <span className="se-hc-eyebrow">{t('hc.hero.eyebrow')}</span>
                        <h1>{t('hc.hero.title')}</h1>
                        <p className="se-hc-hero-sub">{t('hc.hero.sub')}</p>
                        <div className="se-hc-cta-row">
                            <a className="se-hc-cta" href={`mailto:${SUPPORT_EMAIL}`}>
                                {t('hc.hero.cta')}
                            </a>
                            <span className="se-hc-note">{t('hc.hero.note')}</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* content */}
            <main className="se-content">
                {/* App-first disclaimer — the website is a companion, the app is the
                    full experience. */}
                <div className="se-hc-disclaimer" role="alert">
                    <span className="se-hc-disclaimer-icon" aria-hidden="true">⚠</span>
                    <p>
                        <strong>{t('hc.disclaimer.label')}</strong> {t('hc.disclaimer.text')}
                    </p>
                </div>

                <p className="se-hc-lead">{t('hc.lead')}</p>

                <h2 style={{ marginTop: 28 }}>{t('hc.themen')}</h2>
                <div className="se-hc-grid">
                    {TOPICS.map((topic) => {
                        const inner = (
                            <>
                                <span className="se-hc-topic-art">{topic.icon}</span>
                                <span>
                                    <h3>{t(topic.titleKey)}</h3>
                                    <p>{t(topic.descKey)}</p>
                                </span>
                            </>
                        );
                        return topic.to ? (
                            <Link key={topic.titleKey} to={topic.to} className="se-hc-topic">
                                {inner}
                            </Link>
                        ) : (
                            <a key={topic.titleKey} href={topic.href} className="se-hc-topic">
                                {inner}
                            </a>
                        );
                    })}
                </div>

                {/* ── Events erstellen & verwalten ──────────────────────────── */}
                <h2 id="events-erstellen" style={{ marginTop: 44, scrollMarginTop: 80 }}>
                    {t('hc.create.heading')}
                </h2>
                <p>{rich(t('hc.create.intro'))}</p>
                <div className="se-hc-cards">
                    <div className="se-card">
                        <h3>{t('hc.create.private.title')}</h3>
                        <p>{rich(t('hc.create.private.text'))}</p>
                    </div>
                    <div className="se-card">
                        <h3>{t('hc.create.public.title')}</h3>
                        <p>{rich(t('hc.create.public.text'))}</p>
                        <ul>
                            <li>{rich(t('hc.create.public.li1'))}</li>
                            <li>{rich(t('hc.create.public.li2'))}</li>
                            <li>{rich(t('hc.create.public.li3'))}</li>
                        </ul>
                    </div>
                </div>
                <p className="se-callout">{rich(t('hc.create.callout'))}</p>
                <p>{rich(t('hc.create.attendees'))}</p>

                {/* ── Konto verwalten, melden & löschen ─────────────────────── */}
                <h2 id="konto" style={{ marginTop: 44, scrollMarginTop: 80 }}>
                    {t('hc.account.heading')}
                </h2>
                <p>{rich(t('hc.account.intro'))}</p>
                <div className="se-hc-cards">
                    <div className="se-card">
                        <h3>{t('hc.account.report.title')}</h3>
                        <p>{t('hc.account.report.text')}</p>
                        <p>
                            <Link to="/profil-melden">{t('hc.account.report.link')}</Link>
                        </p>
                    </div>
                    <div className="se-card">
                        <h3>{t('hc.account.delete.title')}</h3>
                        <p>{rich(t('hc.account.delete.text'))}</p>
                        <p>
                            <Link to="/profil-loeschen">{t('hc.account.delete.link')}</Link>
                        </p>
                    </div>
                </div>

                <h2 id="faq" style={{ marginTop: 44, scrollMarginTop: 80 }}>
                    {t('hc.faq.heading')}
                </h2>
                <div>
                    {faqs.map((f, i) => (
                        <details className="se-hc-q" key={i}>
                            <summary>{f.q}</summary>
                            <div className="se-hc-a">{f.a}</div>
                        </details>
                    ))}
                </div>

                {/* contact band */}
                <div className="se-hc-contact">
                    <span className="se-hc-contact-art">
                        <SupportMark />
                    </span>
                    <div className="se-hc-contact-body">
                        <h2>{t('hc.contact.heading')}</h2>
                        <p>{t('hc.contact.text')}</p>
                        <a className="se-hc-mail" href={`mailto:${SUPPORT_EMAIL}`}>
                            ✉ {SUPPORT_EMAIL}
                        </a>
                        <p className="se-hc-contact-meta">{t('hc.contact.meta')}</p>
                    </div>
                </div>
            </main>

        </>
    );
}
